import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useProductDirectory } from './use-product-directory';

import type { ReactNode } from 'react';
import type { Organization, OrganizationSelection } from './types';
import type { ProductDirectoryState } from './use-product-directory';

export type SearchMode = 'products' | 'organizations';

type ProductSwitcherContextValue = ProductDirectoryState & {
  product: string;
  organizations: Organization[];
  selectedOrganization?: OrganizationSelection | null;
  onOrganizationChange?: (organization: OrganizationSelection) => void | Promise<void>;
  isPanelOpen: boolean;
  /** The one search in the suite: typed in the context bar, read by the menu it opens. */
  query: string;
  setQuery: (query: string) => void;
  /** Which list the bar is searching; a single-organization member only ever searches products. */
  mode: SearchMode;
  openPanel: (mode: SearchMode) => void;
  togglePanel: (mode: SearchMode) => void;
  closePanel: () => void;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
  /** The context bar itself, so the panel can open at exactly its width. */
  barRef: React.MutableRefObject<HTMLDivElement | null>;
};

const ProductSwitcherContext = createContext<ProductSwitcherContextValue | null>(null);

type ProductSwitcherProviderProps = {
  product: string;
  logoutUrl?: string;
  navigate?: (url: string) => void;
  organizations?: Organization[];
  selectedOrganization?: OrganizationSelection | null;
  onOrganizationChange?: (organization: OrganizationSelection) => void | Promise<void>;
  onOrganizationsMenuOpen?: () => void;
  children: ReactNode;
};

/**
 * One source of truth for the switcher: the panel and the palette are separate surfaces but
 * they answer the same question, so they share one directory fetch, and the shell reads the
 * open state from here to seal the page behind the panel.
 */
export function ProductSwitcherProvider({
  product,
  logoutUrl = '/auth/logout',
  navigate,
  organizations,
  selectedOrganization,
  onOrganizationChange,
  onOrganizationsMenuOpen,
  children,
}: ProductSwitcherProviderProps) {
  const directory = useProductDirectory({ logoutUrl: logoutUrl, navigate: navigate });
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [requestedMode, setRequestedMode] = useState<SearchMode>('products');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const load = directory.load;

  const loadDirectory = useCallback(() => {
    void load();
    if (onOrganizationsMenuOpen) {
      onOrganizationsMenuOpen();
    }
  }, [load, onOrganizationsMenuOpen]);

  // There is nothing to switch between with one organization, so those members search products.
  // Derived rather than stored: a list that arrives late still answers the request that is standing.
  const mode = useMemo<SearchMode>(() => {
    if (requestedMode === 'organizations' && (organizations || []).length > 1) {
      return 'organizations';
    }
    return 'products';
  }, [requestedMode, organizations]);

  const openPanel = useCallback(
    (requested: SearchMode) => {
      // Changing what is being searched drops the search: a product term filtering organizations
      // would answer nothing and read as a broken list.
      if (requested !== requestedMode) {
        setQuery('');
      }
      setRequestedMode(requested);
      setIsPanelOpen(true);
      loadDirectory();
    },
    [loadDirectory, requestedMode]
  );

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    setQuery('');
  }, []);

  // Close or open — never a state updater with effects inside it, which StrictMode runs twice
  // and would fetch the directory two times for one click.
  const togglePanel = useCallback(
    (requested: SearchMode) => {
      if (isPanelOpen) {
        closePanel();
      } else {
        openPanel(requested);
      }
    },
    [isPanelOpen, closePanel, openPanel]
  );

  const value = useMemo(
    () => ({
      ...directory,
      product: product,
      organizations: organizations || [],
      selectedOrganization: selectedOrganization,
      onOrganizationChange: onOrganizationChange,
      isPanelOpen: isPanelOpen,
      query: query,
      setQuery: setQuery,
      mode: mode,
      openPanel: openPanel,
      togglePanel: togglePanel,
      closePanel: closePanel,
      triggerRef: triggerRef,
      barRef: barRef,
    }),
    [
      directory,
      product,
      organizations,
      selectedOrganization,
      onOrganizationChange,
      isPanelOpen,
      query,
      mode,
      openPanel,
      togglePanel,
      closePanel,
    ]
  );

  return <ProductSwitcherContext.Provider value={value}>{children}</ProductSwitcherContext.Provider>;
}

export function useProductSwitcher(): ProductSwitcherContextValue | null {
  return useContext(ProductSwitcherContext);
}
